import {
  CareKindLabels,
  DayCareKindLabels,
  DayCareKind,
  DefaultCareKind,
  CareKindDescriptions,
  AdditionalCareKind,
  SeekerOverhaulCCM1Descriptions,
  SeekerOverhaulCCM1Labels,
} from '@/types/seekerCC';
import { useChildcareDaycareBackup } from '@/components/hooks/useChildcareDaycareBackup';
import useIsSeekerEnrollment2 from '@/components/hooks/useIsSeekerEnrollment2';
import { ServiceType } from '@/__generated__/globalTypes';

type PillOptions = { label: string; value: string; description?: string };

const getPills = ({
  shouldShowDayCareOptions,
  isRemoveDaycareOption,
}: {
  shouldShowDayCareOptions: boolean;
  isRemoveDaycareOption: boolean;
}) => {
  const { isChildcareBackupCare } = useChildcareDaycareBackup();
  const { isSeekerEnrollment2Eligible } = useIsSeekerEnrollment2(ServiceType.CHILD_CARE);

  const DAYCARE_OPTIONS = (): Array<PillOptions> => [
    {
      label: DayCareKindLabels.DAY_CARE_CENTERS,
      value: DayCareKind.DAY_CARE_CENTERS,
    },
    {
      label: DayCareKindLabels.PRESCHOOL_CENTERS,
      value: DayCareKind.PRESCHOOL_CENTERS,
    },
    {
      label: DayCareKindLabels.AFTER_SCHOOL_CENTERS,
      value: DayCareKind.AFTER_SCHOOL_CENTERS,
    },
  ];

  const DEFAULT_OPTIONS = (): Array<PillOptions> => {
    const oneTimePill = {
      label: CareKindLabels.ONE_TIME_BABYSITTERS,
      value: DefaultCareKind.ONE_TIME_BABYSITTERS,
      description: CareKindDescriptions.ONE_TIME_BABYSITTERS,
    };

    const recurringPill = {
      label: CareKindLabels.NANNIES_RECURRING_BABYSITTERS,
      value: DefaultCareKind.NANNIES_RECURRING_BABYSITTERS,
      description: CareKindDescriptions.NANNIES_RECURRING_BABYSITTERS,
    };

    const thirdPill = isChildcareBackupCare
      ? {
          label: CareKindLabels.CHILDCARE_DAYCARE_BACKUP,
          value: AdditionalCareKind.CHILDCARE_DAYCARE_BACKUP,
          description: CareKindDescriptions.CHILDCARE_DAYCARE_BACKUP,
        }
      : {
          label: CareKindLabels.DAY_CARE_CENTERS,
          value: DefaultCareKind.DAY_CARE_CENTERS,
          description: CareKindDescriptions.DAY_CARE_CENTERS,
        };

    if (isSeekerEnrollment2Eligible) {
      return [
        {
          label: SeekerOverhaulCCM1Labels.NANNIES_RECURRING_BABYSITTERS,
          value: DefaultCareKind.NANNIES_RECURRING_BABYSITTERS,
          description: SeekerOverhaulCCM1Descriptions.NANNIES_RECURRING_BABYSITTERS,
        },
        {
          label: SeekerOverhaulCCM1Labels.ONE_TIME_BABYSITTERS,
          value: DefaultCareKind.ONE_TIME_BABYSITTERS,
          description: SeekerOverhaulCCM1Descriptions.ONE_TIME_BABYSITTERS,
        },
        {
          label: SeekerOverhaulCCM1Labels.CHILDCARE,
          value: DefaultCareKind.CHILDCARE,
          description: SeekerOverhaulCCM1Descriptions.CHILDCARE,
        },
        {
          label: SeekerOverhaulCCM1Labels.DAY_CARE_CENTERS,
          value: DayCareKind.DAY_CARE_CENTERS,
          description: SeekerOverhaulCCM1Descriptions.DAY_CARE_CENTERS,
        },
      ];
    }

    if (isRemoveDaycareOption) {
      return [recurringPill, oneTimePill];
    }

    return [recurringPill, oneTimePill, thirdPill];
  };

  if (shouldShowDayCareOptions) {
    return DAYCARE_OPTIONS();
  }

  return DEFAULT_OPTIONS();
};

export default getPills;
